import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Col, Row } from 'react-bootstrap'
import { useMediaQuery } from 'react-responsive';

import { isTabletOrMobileQuery } from '../../utils';
import * as routes from '../../routes';
import { RootState } from '../../store';
import { addToast } from '../../store/misc/misc';
import { ToastType } from '../../store/misc/interfaces';
import { setPaymentMethod } from '../../store/paymentData';
import StageNavigation from '../shared/StageNavigation';
import PaymentDetails from './PaymentDetails';
import PaymentTypeSelector, { PaymentType, paymentTypes } from './PaymentTypeSelector';
import { usePaymentValidators } from '../../hooks/validators';
import { IRegistration } from '../../store/registrations/interfaces';
import { getNewRegistrations } from '../../store/registrations/selectors';
import DiscountCodeInput from './DiscountCodeInput';
import { DiscountCodeModes } from '../../store/program/interfaces';
import { useCheckPaymentStageSkip, useNumPaymentsEnabled } from '../../functions/registrations/checkPaymentStageSkip';
import LoadedViaTokenIndicator from '../shared/LoadedViaTokenIndicator'

const PaymentStage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { payment_configuration } = useSelector(
        (state: RootState) => state.program.data,
    );
    const { method: selectedPaymentMethod } = useSelector(
        (state: RootState) => state.paymentData,
    );
    const registrations = useSelector(
        (state: RootState) => state.registrations.list,
    );
    const newRegistrations = useSelector(getNewRegistrations);
    const appliedDiscountCodes = registrations.reduce((acc, reg) => {
        reg.discountCodes.forEach(dc => {
            if (!acc.includes(dc.code)) {
                acc.push(dc.code);
            }
        });
        return acc;
    }, [] as string[]);

    const { basic_fee } = useSelector((state: RootState) => state.program.data);

    const enabledPaymentMethods = useNumPaymentsEnabled();

    const isTabletOrMobile = useMediaQuery(isTabletOrMobileQuery);
    const { data: program } = useSelector((state: RootState) => state.program);
    const { data: institution } = useSelector((state: RootState) => state.institution);

    const skipPaymentStage = useCheckPaymentStageSkip();

    useEffect(() => {
        // Update the document title using the browser API
        document.title = `${program.name}: Bezahlung | Feripro ${institution.name}`;
    }, [program.name, institution.name]);

    useEffect(() => {
        // Redirection logic based on conditions

        if (skipPaymentStage) {
            let message = '';

            if (!payment_configuration.enabled && enabledPaymentMethods === 0) {
                message = 'In diesem Programm kann keine Bezahlmethode hinterlegt werden.';
            }

            if (basic_fee) {
                message = 'Für dieses Programm wird eine Grundgebühr erhoben!';
            }

            if (message) {
                dispatch(
                    addToast({
                        title: 'Info',
                        body: message,
                        type: ToastType.Info,
                    })
                );
            }

            dispatch(setPaymentMethod('free'));
            // When skipping this stage we shouldn't keep it in the browser history
            navigate(routes.VERIFY.path, { replace: true });
        }
        // Only clear payment method if we're in a state where no payment method should be selected
        // (e.g., when payment configuration changes in a way that invalidates current selection)
        // Don't clear it just because we're returning to this stage
    }, [payment_configuration.enabled, enabledPaymentMethods, registrations, basic_fee, dispatch, navigate, skipPaymentStage]);

    let header = <h3>Methoden</h3>;
    let paymentMethodsBody = (
        <Card>
            <Card.Header>
                Bitte beachten: Die Bezahlung wird direkt beim Veranstalter
                geregelt.
            </Card.Header>
            <Card.Body>
                Der Prozess kann nun abgeschlossen werden.
            </Card.Body>
        </Card>
    );

    const {
        addValidator,
        removeValidator,
        hasValidationErrors,
    } = usePaymentValidators(enabledPaymentMethods);

    if (newRegistrations.length > 0 && newRegistrations.every((reg: IRegistration) => reg.event.has_external_payment) && !basic_fee) {
        header = <h3>Wichtiger Hinweis</h3>;
        dispatch(setPaymentMethod('free'));
    } else {
        if (enabledPaymentMethods === 1) {
            const onlyPaymentType = paymentTypes.find(pType =>
                pType.enabled(payment_configuration),
            ) as PaymentType;
            if (selectedPaymentMethod !== onlyPaymentType.code) {
                dispatch(setPaymentMethod(onlyPaymentType.code));
            }
        }
        paymentMethodsBody = (
            <PaymentTypeSelector
                paymentConfigs={payment_configuration}
                addValidator={addValidator}
                removeValidator={removeValidator}
            />
        );
    }

    return (
        <>
            <Card>
                <Card.Body>
                    <Card.Title>
                        <h2 aria-level={1}>
                            Bezahlung
                        </h2>
                        <LoadedViaTokenIndicator />
                    </Card.Title>
                    <Card.Text>
                        {enabledPaymentMethods > 1 ? (
                            <>
                                Bitte die bevorzugte Zahlungsmethode wählen. Sollte eine Zahlung nicht
                                notwendig sein oder die Veranstaltung extern bezahlt werden, dient
                                diese Seite lediglich als Information. <br /><br />Notwendige Felder sind mit einem *
                                markiert.
                            </>
                        ) : (
                            <>
                                Die verfügbare Zahlungsmethode wurde automatisch ausgewählt.
                                Sollte eine Zahlung nicht notwendig sein oder die Veranstaltung extern bezahlt werden,
                                dient diese Seite lediglich als Information. <br /><br />Notwendige Felder sind mit einem *
                                markiert.
                            </>
                        )}
                    </Card.Text>
                </Card.Body>
            </Card>
            <br />
            <Row>
                <Col sm={12} md={6} className={isTabletOrMobile ? 'border-bottom' : ''}>
                    <h3>Übersicht</h3>
                    <PaymentDetails registrations={registrations} isVerifyStage={false} />
                    {program.discountcode_mode !== DiscountCodeModes.DISCOUNTCODE_OFF &&
                        <DiscountCodeInput registrations={registrations} />
                    }
                </Col>
                <Col sm={12} md={6} className={isTabletOrMobile ? 'pt-3' : ''}>
                    {header}
                    {paymentMethodsBody}
                </Col>
            </Row>
            <StageNavigation
                backward={{
                    text: 'Zurück',
                    handler: () => navigate(routes.PARTICIPANT.path),
                }}
                forward={{
                    text: routes.VERIFY.longName,
                    handler: async () => {
                        if (selectedPaymentMethod === '') {
                            dispatch(
                                addToast({
                                    title: 'Fehler',
                                    body: 'Bitte eine Zahlungsmethode auswählen.',
                                    type: ToastType.Danger,
                                }),
                            );
                        } else if (await hasValidationErrors()) {
                            dispatch(
                                addToast({
                                    title: 'Fehler',
                                    body: 'Bitte die angegebenen Daten nochmal überprüfen.',
                                    type: ToastType.Danger,
                                }),
                            );
                        } else {
                            navigate(routes.VERIFY.path);
                        }
                    },
                }}
            />
        </>
    );
};

export default PaymentStage;
